import { useMemo, useRef, type ReactNode } from 'react'
import { Check, User, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  PERSONALITY_DIMENSIONS,
  PROFILE_LABELS,
  PROFILE_ICONS,
  parsePersonalityTraits,
} from '@/frontend/pages/Dashboard/DigitalWorkers/constants'
import { PROVIDER_ICONS_SMALL, FallbackIcon } from '@/frontend/components/DigitalWorkers/providerIcons'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import { isComposioIntegration, type ComposioOrgIntegration, type OrgIntegration } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { useBayerDither } from '@/frontend/hooks/useBayerDither'
import { agentHue, agentHueStyle } from '@/frontend/lib/agentHue'
import { oklchToRgb, type Rgb } from '@/frontend/lib/oklch'

const PERSONALITY_ICON_BY_TRAIT: Record<string, LucideIcon> = PERSONALITY_DIMENSIONS.reduce<Record<string, LucideIcon>>((acc, dimension) => {
  dimension.options.forEach((option) => {
    acc[option.value] = option.icon
  })
  return acc
}, {})

type ProfileStatus = 'creating' | 'online' | 'failed'

interface ChannelInfo {
  key: string
  label: string
  icon: ReactNode
}

interface Props {
  name: string
  fleetProfile: string
  /**
   * Persisted worker id: rotates the banner gradient/dither to the agent's
   * stable identity hue (B.4) via --agent-hue. Without a seed (creation flow,
   * no id yet) the card keeps today's fixed indigo→purple look.
   */
  hueSeed?: string | null
  jobTitle?: string | null
  personality?: string | null
  personalityCompleted?: boolean
  contactName?: string | null
  avatarUrl?: string | null
  avatarCompleted?: boolean
  revealAvatar?: boolean
  integrations?: OrgIntegration[]
  showProgress?: boolean
  status?: ProfileStatus | null
  actions?: ReactNode
  channels?: ChannelInfo[]
  channelsEmptyCta?: { label: string; href: string }
  className?: string
}

const STATUS_BADGES: Record<ProfileStatus, { label: string; dotColor: string; pulse: boolean }> = {
  creating: { label: 'Em criação', dotColor: 'bg-white', pulse: true },
  online: { label: 'Online', dotColor: 'bg-green-400', pulse: true },
  failed: { label: 'Falha', dotColor: 'bg-red-400', pulse: false },
}

interface ChecklistItem {
  key: string
  label: string
  value: string | null
  done: boolean
}

const MAX_INTEGRATION_ICONS = 6

export default function WorkerProfileCard({
  name,
  fleetProfile,
  hueSeed,
  jobTitle,
  personality,
  personalityCompleted,
  contactName,
  avatarUrl,
  avatarCompleted,
  revealAvatar = false,
  integrations = [],
  showProgress = true,
  status = 'creating',
  actions,
  channels,
  channelsEmptyCta,
  className,
}: Props) {
  const statusBadge = status ? STATUS_BADGES[status] : null
  const trimmedName = name.trim()
  const displayName = trimmedName || 'Seu colaborador digital'
  const namePending = !trimmedName
  const cargoLabel = fleetProfile ? PROFILE_LABELS[fleetProfile] ?? fleetProfile : null
  const ProfileIcon = fleetProfile ? PROFILE_ICONS[fleetProfile] : null

  const shouldShowAvatar = revealAvatar && Boolean(avatarUrl)
  const personalityTraits = parsePersonalityTraits(personality)

  const checklist: ChecklistItem[] = [
    { key: 'identity', label: 'Nome e cargo', value: trimmedName || cargoLabel || null, done: !!trimmedName && !!fleetProfile },
    { key: 'contact', label: 'Responsável', value: contactName?.trim() || null, done: !!contactName?.trim() },
    { key: 'personality', label: 'Personalidade', value: personality?.trim() || null, done: personalityCompleted ?? !!personality?.trim() },
    { key: 'avatar', label: 'Aparência', value: avatarUrl ? 'Definido' : null, done: avatarCompleted ?? !!avatarUrl },
    { key: 'integrations', label: 'Integrações', value: integrations.length > 0 ? `${integrations.length} ${integrations.length === 1 ? 'fonte' : 'fontes'}` : null, done: integrations.length > 0 },
  ]

  const completed = checklist.filter((item) => item.done).length
  const completionPercent = Math.round((completed / checklist.length) * 100)
  const visibleIntegrations = integrations.slice(0, MAX_INTEGRATION_ICONS)
  const extraIntegrations = Math.max(0, integrations.length - MAX_INTEGRATION_ICONS)

  const bannerCanvasRef = useRef<HTMLCanvasElement>(null)
  // Banner grain: the fixed endpoints (#A78BFA → #D8B4E6) sit at
  // oklch(0.71 0.16 293.5) → oklch(0.82 0.08 316.5); with a hueSeed the same
  // L/C rotate to the agent's hue (B.4) — the Bayer look stays, only the hue moves.
  const bannerDitherColor = useMemo(() => {
    const hue = hueSeed ? agentHue(hueSeed) : null
    const start: Rgb = hue === null ? [167, 139, 250] : oklchToRgb(0.71, 0.16, hue)
    const end: Rgb = hue === null ? [216, 180, 230] : oklchToRgb(0.82, 0.08, hue + 23)
    return (nx: number): [number, number, number, number] => [
      start[0] + (end[0] - start[0]) * nx,
      start[1] + (end[1] - start[1]) * nx,
      start[2] + (end[2] - start[2]) * nx,
      110,
    ]
  }, [hueSeed])
  useBayerDither(bannerCanvasRef, { pixelSize: 2, color: bannerDitherColor })

  return (
    <div
      className={cn('w-full', className)}
      // Sets --agent-hue for the whole card (see agentHueStyle) so the banner
      // gradient below and any bg-agent accent inside resolve to this agent.
      style={hueSeed ? agentHueStyle(hueSeed) : undefined}
      data-testid="worker-profile-card"
    >
      <div className="overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-sm">
        {/* Header banner with dithered avatar-style gradient. With a hueSeed the
            stops keep the fixed gradient's L/C (indigo-950 → violet-900 →
            purple-900 ≈ hue −12 / 0 / +11) and rotate with --agent-hue. */}
        <div
          className={cn(
            'relative h-32 overflow-hidden bg-gradient-to-br',
            hueSeed
              ? 'from-[oklch(0.26_0.09_calc(var(--agent-hue)_-_12))] via-[oklch(0.38_0.18_var(--agent-hue))] to-[oklch(0.38_0.17_calc(var(--agent-hue)_+_11))]'
              : 'from-indigo-950 via-violet-900 to-purple-900',
          )}
        >
          <canvas
            ref={bannerCanvasRef}
            aria-hidden="true"
            className="absolute inset-0 h-full w-full pointer-events-none opacity-60"
            style={{ imageRendering: 'pixelated' }}
          />
          {statusBadge && (
            <div className="absolute right-4 top-4 z-10">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm"
                data-testid="worker-profile-status-badge"
              >
                <span className="relative flex h-2 w-2">
                  {statusBadge.pulse && (
                    <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full opacity-70', statusBadge.dotColor)} />
                  )}
                  <span className={cn('relative inline-flex h-2 w-2 rounded-full', statusBadge.dotColor)} />
                </span>
                {statusBadge.label}
              </span>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 -mt-12">
          {/* Avatar + personality badges */}
          <div className="mb-4 flex items-end gap-2">
            <div className="relative h-24 w-24 shrink-0">
              {shouldShowAvatar ? (
                <div className="h-full w-full overflow-hidden rounded-full border-4 border-card bg-card shadow-lg">
                  <img
                    key={avatarUrl}
                    src={avatarUrl ?? undefined}
                    alt={`Avatar de ${displayName}`}
                    className="h-full w-full object-cover animate-avatar-breathing"
                  />
                </div>
              ) : (
                <div
                  className={cn(
                    'flex h-full w-full items-center justify-center overflow-hidden rounded-full border-4 border-card bg-gradient-to-br shadow-lg',
                    hueSeed
                      ? 'from-[oklch(0.26_0.09_calc(var(--agent-hue)_-_12))] to-[oklch(0.38_0.17_calc(var(--agent-hue)_+_11))]'
                      : 'from-indigo-950 to-purple-900',
                  )}
                >
                  <img
                    src="/images/avatar-silhouette.png"
                    alt=""
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
            </div>

            {personalityTraits.length > 0 && (
              <TooltipProvider delayDuration={150}>
                <div
                  className="flex flex-wrap items-center gap-1 pb-2"
                  data-testid="worker-profile-personality-badges"
                >
                  {personalityTraits.map((trait) => {
                    const Icon = PERSONALITY_ICON_BY_TRAIT[trait]
                    if (!Icon) return null
                    return (
                      <Tooltip key={trait}>
                        <TooltipTrigger asChild>
                          <span
                            tabIndex={0}
                            aria-label={trait}
                            className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-violet-500/10 text-violet-600 shadow-sm ring-1 ring-violet-500/20 dark:bg-violet-500/15 dark:text-violet-300"
                          >
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">{trait}</TooltipContent>
                      </Tooltip>
                    )
                  })}
                </div>
              </TooltipProvider>
            )}

            {actions && (
              <div className="ml-auto flex flex-wrap items-center gap-2 pb-2">
                {actions}
              </div>
            )}
          </div>

          {/* Name + role */}
          <h2
            className={cn(
              'mb-1 truncate text-2xl font-semibold tracking-tight',
              namePending ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {displayName}
          </h2>
          <div className="mb-5 flex items-center gap-1.5 text-sm">
            {ProfileIcon && <ProfileIcon className="h-3.5 w-3.5 shrink-0 text-violet-500" />}
            <span className={cn('truncate', cargoLabel ? 'text-muted-foreground' : 'italic text-muted-foreground/70')}>
              {cargoLabel ?? 'Cargo a definir'}
              {jobTitle && cargoLabel && jobTitle !== cargoLabel && ` · ${jobTitle}`}
            </span>
            {contactName?.trim() && (
              <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <User className="h-3 w-3" />
                <span className="max-w-[100px] truncate">{contactName.trim()}</span>
              </span>
            )}
          </div>

          {showProgress && (
            <div className="mb-5 space-y-4 border-t border-b border-border py-4">
              <div>
                <div className="mb-1.5 flex items-center justify-between text-[11px]">
                  <span className="font-medium text-muted-foreground">{completed} de {checklist.length} preenchidos</span>
                  <span className="font-semibold text-foreground">{completionPercent}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 transition-all duration-500 ease-out"
                    style={{ width: `${completionPercent}%` }}
                  />
                </div>
              </div>

              <ul className="relative space-y-1.5">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-[9px] top-2.5 bottom-2.5 w-0.5 bg-muted-foreground/25"
              />
                {checklist.map((item, index) => (
                  <li key={item.key} className="flex items-center gap-2.5 text-xs">
                    <span
                      className={cn(
                        'relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ring-1',
                        item.done
                          ? 'bg-violet-500 text-white ring-violet-500'
                          : 'bg-card text-muted-foreground/60 ring-muted-foreground/30',
                      )}
                    >
                      {index + 1}
                    </span>
                    <span className={cn('shrink-0 font-medium', item.done ? 'text-foreground' : 'text-muted-foreground')}>{item.label}</span>
                    {item.done && (
                      <span
                        aria-hidden="true"
                        className="ml-auto flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-500 text-white"
                      >
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={cn('flex items-center justify-between gap-3', !showProgress && 'mt-5 border-t border-border pt-5')}>
            <span className="text-xs font-medium text-muted-foreground">
              {channels !== undefined ? 'Canais de comunicação' : 'Conectado a'}
            </span>
            {channels !== undefined ? (
              channels.length > 0 ? (
                <div className="flex items-center -space-x-1.5">
                  {channels.map((channel) => (
                    <span
                      key={channel.key}
                      title={channel.label}
                      className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-background shadow-sm"
                    >
                      {channel.icon}
                    </span>
                  ))}
                </div>
              ) : channelsEmptyCta ? (
                <a
                  href={channelsEmptyCta.href}
                  className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 transition-colors hover:text-violet-700 dark:text-violet-400"
                >
                  {channelsEmptyCta.label}
                  <span aria-hidden="true">→</span>
                </a>
              ) : (
                <span className="text-xs italic text-muted-foreground/70">Nenhum ainda</span>
              )
            ) : visibleIntegrations.length > 0 ? (
              <div className="flex items-center -space-x-1.5">
                {visibleIntegrations.map((integration) => (
                  <span
                    key={integration.id}
                    title={integration.provider_label}
                    className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-background shadow-sm"
                  >
                    {isComposioIntegration(integration) ? (
                      <ComposioAppIcon
                        logo={(integration as ComposioOrgIntegration).logo}
                        name={integration.provider_label}
                        className="h-3.5 w-3.5"
                      />
                    ) : (
                      PROVIDER_ICONS_SMALL[integration.provider] || <FallbackIcon />
                    )}
                  </span>
                ))}
                {extraIntegrations > 0 && (
                  <span className="flex h-7 min-w-7 items-center justify-center rounded-full border-2 border-card bg-background px-1.5 text-[10px] font-semibold text-muted-foreground shadow-sm">
                    +{extraIntegrations}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs italic text-muted-foreground/70">Nenhuma ainda</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
